// src/lib/mso-diff.ts
//
// ★★ ANNEX A 22 — the words for a rule definition, and the comparison of two versions' decision
// tables. Pure functions: nothing here fetches, and nothing here decides what a rule MEANS.
import { canonicalText } from './mso-canonical'
import {
  humaniseToken,
  MSO_COMPONENT_LABELS,
  MSO_FIELD_LABELS,
  MSO_OPERATOR_LABELS,
  basisPointsLabel,
} from './mso-vocabulary'
import type { MsoCondition, MsoDecisionRow, MsoRuleDefinition, MsoScalar } from './types'

function scalarText(value: MsoScalar): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return /^[A-Z0-9_]+$/.test(value) ? humaniseToken(value) : value
}

/** "Beneficiary type is Principal", "Cap status is one of Reached, Not reached". */
export function conditionText(condition: MsoCondition): string {
  const field = MSO_FIELD_LABELS[condition.field] ?? humaniseToken(condition.field)
  const op = MSO_OPERATOR_LABELS[condition.op] ?? condition.op
  if (condition.op === 'present' || condition.op === 'absent') return `${field} ${op}`
  const value = condition.value
  if (Array.isArray(value)) return `${field} ${op} ${(value as readonly MsoScalar[]).map(scalarText).join(', ')}`
  if (value === undefined) return `${field} ${op}`
  return `${field} ${op} ${scalarText(value as MsoScalar)}`
}

/** "Every component 15.00%" or "Bill amount 15.00%, Ward charges 40.00%". */
export function sharesText(row: MsoDecisionRow): string {
  return row.shares
    .map((s) => `${MSO_COMPONENT_LABELS[s.component] ?? humaniseToken(s.component)} ${basisPointsLabel(s.officerBasisPoints)}`)
    .join(', ')
}

export type RowChange = 'ADDED' | 'REMOVED' | 'CHANGED' | 'UNCHANGED'

export interface RowDiff {
  readonly id: string
  readonly change: RowChange
  readonly before: MsoDecisionRow | null
  readonly after: MsoDecisionRow | null
  /** What differs, in words, for a CHANGED row ("conditions", "shares", "label"). */
  readonly what: readonly string[]
}

/**
 * Compare two decision tables by ROW ID. Order is the left table's, then rows only the right has.
 *
 * ⚠ A row is "changed" when its label, its conditions or its shares differ; the comparison uses
 * canonical text so a re-keyed object is not reported as an edit. Row order within a table is
 * not a difference: a decision table has no order, only a unique match.
 */
export function diffDecisionTables(
  left: readonly MsoDecisionRow[],
  right: readonly MsoDecisionRow[],
): readonly RowDiff[] {
  const rightById = new Map(right.map((r) => [r.id, r]))
  const leftIds = new Set(left.map((r) => r.id))
  const out: RowDiff[] = []
  for (const before of left) {
    const after = rightById.get(before.id) ?? null
    if (after === null) {
      out.push({ id: before.id, change: 'REMOVED', before, after: null, what: [] })
      continue
    }
    const what: string[] = []
    if (before.label !== after.label) what.push('label')
    if (canonicalText(before.when) !== canonicalText(after.when)) what.push('conditions')
    if (canonicalText(before.shares) !== canonicalText(after.shares)) what.push('shares')
    out.push({ id: before.id, change: what.length === 0 ? 'UNCHANGED' : 'CHANGED', before, after, what })
  }
  for (const after of right) {
    if (!leftIds.has(after.id)) out.push({ id: after.id, change: 'ADDED', before: null, after, what: [] })
  }
  return out
}

/** Differences outside the table that an officer should also see. */
export function diffDefinitionSettings(left: MsoRuleDefinition, right: MsoRuleDefinition): readonly string[] {
  const out: string[] = []
  if (left.precedence !== right.precedence) out.push(`Precedence ${left.precedence} to ${right.precedence}`)
  if (left.rounding.mode !== right.rounding.mode) out.push(`Rounding ${left.rounding.mode} to ${right.rounding.mode}`)
  if (canonicalText(left.applicability) !== canonicalText(right.applicability)) out.push('Applicability differs')
  if (canonicalText(left.requiredInputs) !== canonicalText(right.requiredInputs)) out.push('Required inputs differ')
  return out
}
